import { BannerDirective } from '@myre/ui-primitive-components';
import { TranslatePipe } from '@myre/ui-primitive-core';
import { TooltipComponent } from '@myre/ui-primitive-tooltip';

import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  isDevMode,
  Output,
  TemplateRef,
  ViewChild,
  ViewChildren,
  type AfterContentInit,
  type AfterViewChecked,
  type OnInit,
  type QueryList,
} from '@angular/core';
import {
  GoogleMap,
  MapInfoWindow,
  MapMarker,
  MapTransitLayer,
} from '@angular/google-maps';

import { type ICoordinate } from './googlemap.interface';
import { GoogleMapsApiService } from './googlemapsapi.service';

@Directive({ selector: '[myreGmTooltipTemplate]' })
export class MyreGmTooltipTemplateDirective {
  public templateRef = inject<TemplateRef<any>>(TemplateRef);
}

@Component({
  selector: 'myre-googlemap',
  templateUrl: './googlemap.component.html',
  styleUrls: ['./googlemap.component.scss'],
  imports: [
    GoogleMap,
    MapTransitLayer,
    MapMarker,
    MapInfoWindow,
    NgTemplateOutlet,
    BannerDirective,
    TranslatePipe,
    TooltipComponent,
  ],
})
export class GooglemapComponent
  implements OnInit, AfterViewChecked, AfterContentInit
{
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _googleMapsApiService = inject(GoogleMapsApiService);

  @ViewChild('googleMap', { static: false })
  public googleMap: GoogleMap;

  @ViewChildren(MapMarker)
  public markers: QueryList<MapMarker>;

  @ViewChildren(MapInfoWindow)
  public infoWindows: QueryList<MapInfoWindow>;

  @ViewChildren(TooltipComponent)
  public tooltips: QueryList<TooltipComponent>;

  @ViewChildren('content')
  public contents: QueryList<ElementRef>;

  @ContentChild(MyreGmTooltipTemplateDirective, { static: false })
  public myreGmTooltipTemplate: MyreGmTooltipTemplateDirective;

  @Input()
  public set coordinates(coordinates: ICoordinate[]) {
    if (coordinates.length) {
      this._coordinates = coordinates.map(c => {
        return {
          ...c,
          zIndex: typeof c.zIndex === 'number' ? c.zIndex : 1,
        };
      });
    }
  }

  public get coordinates(): ICoordinate[] {
    return this._coordinates;
  }

  @Input()
  public width?: number;

  @Input()
  public height?: number;

  @Input()
  public mapApiKey: string;

  @Output()
  public pinClick = new EventEmitter<any>();

  @Output()
  public pinOver = new EventEmitter<any>();

  public get mapsLoaded(): boolean {
    return this._googleMapsApiService.isGoogleMapsApiLoaded;
  }

  public get mapsError(): boolean {
    return this._googleMapsApiService.isGoogleMapsApiOnError;
  }

  public tooltipTemplate: TemplateRef<any>;

  private _mapsInitialized: boolean = false;
  private _coordinates: ICoordinate[];

  public async ngOnInit(): Promise<void> {
    try {
      await this._googleMapsApiService.initialize(this.mapApiKey);

      this.height ||= 300;
    } catch (err) {
      // error state is stored in GoogleMapsApiService;

      if (isDevMode()) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }

    this._changeDetectorRef.markForCheck();
  }

  public ngAfterContentInit(): void {
    if (this.myreGmTooltipTemplate) {
      this.tooltipTemplate = this.myreGmTooltipTemplate.templateRef;
    }
  }

  public ngAfterViewChecked(): void {
    if (
      this._googleMapsApiService.isGoogleMapsApiLoaded &&
      !this._mapsInitialized &&
      this.googleMap?.googleMap
    ) {
      this._initializeMap();

      this._mapsInitialized = true;
    }
  }

  public onMarkerClick(event: any): void {
    this.pinClick.emit(event);
  }

  // Note: events on marker are defined only at initialization.
  // If we add markers after component is initialized, this component needs to be reworked.
  private _initializeMap(): void {
    const map = this.googleMap.googleMap;
    const bounds = new google.maps.LatLngBounds();

    if (this.coordinates.length === 1) {
      const c = this.coordinates[0];
      map.setCenter(new google.maps.LatLng(c.latitude, c.longitude));
      map.setZoom(15);
    } else {
      for (const c of this.coordinates) {
        bounds.extend(new google.maps.LatLng(c.latitude, c.longitude));
      }

      // Set viewport in the next iteration of the event loop
      setTimeout(() => {
        map.fitBounds(bounds);
      });
    }

    for (let i = 0; i < this.markers.toArray().length; i++) {
      const marker = this.markers.toArray()[i].marker;
      const infoWindow = this.infoWindows.toArray()[i].infoWindow;

      marker.addListener('mouseover', () => {
        const tooltip = this.tooltips.toArray()[i];

        if (tooltip && !tooltip.show) {
          this.pinOver.emit(this.coordinates[i]);

          tooltip.show = true;

          infoWindow.setOptions({
            content: this.contents.toArray()[i].nativeElement,
          });
          infoWindow.open(this.googleMap.googleMap, marker);

          this._changeDetectorRef.markForCheck();
        }
      });

      marker.addListener('mouseout', () => {
        const tooltip = this.tooltips.toArray()[i];

        if (tooltip) {
          tooltip.show = false;
        }

        infoWindow.close();
        this._changeDetectorRef.markForCheck();
      });
    }
  }
}
